import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { from, of } from 'rxjs';
import { PersistentStorageService } from '../../persistent-storage/persistent-storage.service';
import { RoamingQcStoreFacade } from './roaming-qc-db.facade';
import {
  AcquireLockAndClearInProgressDetails,
  AcquireLockAndClearTransactionLogRecords,
  AcquireLockAndFetchTransactionLogRecords,
  AcquireLockAndThenInitStore,
  AcquireLockAndUpdateEpfDetailsAndRecentCategoryList,
  AcquireLockAndUpdateInProgressDetails,
  ClearInProgressDetailsAndReleaseLock,
  ClearTransactionLogRecordsAndReleaseLock,
  FetchAndUpdateTransactionLogRecordsAndReleaseLock,
  InitStoreAndThenReleaseLock,
  ReleaseRoamingQcLock,
  RoamingQcNoAction,
  RoamingQcStoreActionTypes,
  UpdateEpfDetailsAndRecentCategoryListAndReleaseLock,
  UpdateInitialRoundInfoAndReleaseLock,
  UpdateInProgressDetailsAndReleaseLock,
  UpdateStoreAndReleaseLock,
  UpdateStoreAndReleaseLockAndThenGoToInspection,
  UpdateTransactionLogRecordsAndReleaseLock,
} from './roaming-qc-db.actions';
import { EPF_CACHE_STORAGE_TIME, getRoamingQcStoreInitalState } from './roaming-qc-db.reducer';
import produce from 'immer';
import { InitStoreFacade } from '../init-db/init-db.facade';
import { InputAppClientDBFacade } from '../input-app-client-db/input-app-client-db.facade';
import { ModalController } from '@ionic/angular';
import { InputAppAPIHelperUtil, RoamingQcUtil } from '@monorepo/utils';
import { InputAppDataService, CustomLoggerService } from '@monorepo/services';
import { TranslateService } from '@ngx-translate/core';
import { TRESPONSE_RoamingQcTransactions } from '@monorepo/types';

@Injectable({
  providedIn: 'root',
})
export class RoamingQcStoreEffects {
  constructor(
    private action$: Actions,
    private persistentStorageService: PersistentStorageService,
    private roamingQcStoreFacade: RoamingQcStoreFacade,
    private initStoreFacade: InitStoreFacade,
    private inputAppClientDBFacade: InputAppClientDBFacade,
    private inputAppAPIHelperUtil: InputAppAPIHelperUtil,
    private modalCtrl: ModalController,
    private inputAppDataService: InputAppDataService,
    private translateService: TranslateService,
    private logger: CustomLoggerService,
  ) {}

  AcquireLockAndThenInitStore$ = createEffect(() => {
    return this.action$.pipe(
      ofType<AcquireLockAndThenInitStore>(RoamingQcStoreActionTypes.AcquireLockAndThenInitStore),
      switchMap((action) => {
        return this.roamingQcStoreFacade.acquireLock(action.lockFor).pipe(
          take(1),
          map((res) => {
            if (res.status === 'SUCCESS') {
              return new InitStoreAndThenReleaseLock(action.lockFor);
            } else {
              this.inputAppAPIHelperUtil.showPopup(this.translateService.instant('common.error'), res.message);
              return new RoamingQcNoAction();
            }
          }),
        );
      }),
    );
  });

  InitStoreAndThenReleaseLock$ = createEffect(() => {
    return this.action$.pipe(
      ofType<InitStoreAndThenReleaseLock>(RoamingQcStoreActionTypes.InitStoreAndThenReleaseLock),
      switchMap((action) => {
        return this.initStoreFacade.getNextCacheResetTimestamp().pipe(
          take(1),
          map((nextCacheRestTimestamp) => [action, nextCacheRestTimestamp] as const),
        );
      }),
      switchMap(([action, nextCacheRestTimestamp]) => {
        return this.roamingQcStoreFacade.getAllStationIdsAndShifts().pipe(
          take(1),
          map(({ allStaionIds, allShifts }) => [action, nextCacheRestTimestamp, allStaionIds, allShifts] as const),
        );
      }),
      switchMap(([action, nextCacheRestTimestamp, allStaionIds, allShifts]) =>
        from(this.persistentStorageService.get('[APP][INPUT_APP_ROAMING_QC_DB]')).pipe(
          take(1),
          switchMap((res) => {
            if (res && res.nextCacheResetTimestamp === nextCacheRestTimestamp)
              return of(new UpdateStoreAndReleaseLock(res));
            else {
              const roamingQcInitialState = getRoamingQcStoreInitalState(nextCacheRestTimestamp);
              let updatedRoamingQcState = roamingQcInitialState.roamingQcDB;
              if (res) {
                if (allShifts && allStaionIds) {
                  updatedRoamingQcState = produce(updatedRoamingQcState, (draft) => {
                    allStaionIds.forEach((stationId) => {
                      Object.keys(allShifts).forEach((shift) => {
                        if (!draft.dailyCache.lineShiftWiseRoundInfo.lineMap[stationId]) {
                          draft.dailyCache.lineShiftWiseRoundInfo.lineMap[stationId] = {
                            shiftMap: {
                              [shift]: 0,
                            },
                          };
                        } else draft.dailyCache.lineShiftWiseRoundInfo.lineMap[stationId].shiftMap[shift] = 0;
                      });
                    });
                  });
                }
                const currentTime = new Date().getTime();
                Object.entries(res.persistanceCache.epfDetails.lineMap).forEach(([line, shiftWiseEpfCache]) => {
                  Object.entries(shiftWiseEpfCache.shiftMap).forEach(([shift, epfDataList]) => {
                    epfDataList.forEach((epfDTO) => {
                      if (epfDTO.lastUpdatedTime + EPF_CACHE_STORAGE_TIME >= currentTime) {
                        updatedRoamingQcState = produce(updatedRoamingQcState, (draft) => {
                          if (!draft.persistanceCache.epfDetails.lineMap[line]) {
                            draft.persistanceCache.epfDetails.lineMap[line] = {
                              shiftMap: {
                                [shift]: [epfDTO],
                              },
                            };
                          } else if (!draft.persistanceCache.epfDetails.lineMap[line].shiftMap[shift]) {
                            draft.persistanceCache.epfDetails.lineMap[line].shiftMap[shift] = [epfDTO];
                          } else draft.persistanceCache.epfDetails.lineMap[line].shiftMap[shift].push(epfDTO);
                        });
                      }
                    });
                  });
                });
              }
              return from(
                this.persistentStorageService.set('[APP][INPUT_APP_ROAMING_QC_DB]', updatedRoamingQcState),
              ).pipe(
                take(1),
                map(() => {
                  return new UpdateStoreAndReleaseLock(updatedRoamingQcState);
                }),
              );
            }
          }),
        ),
      ),
    );
  });

  AcquireLockAndUpdateEpfDetailsAndRecentCategoryList$ = createEffect(() => {
    return this.action$.pipe(
      ofType<AcquireLockAndUpdateEpfDetailsAndRecentCategoryList>(
        RoamingQcStoreActionTypes.AcquireLockAndUpdateEpfDetailsAndRecentCategoryList,
      ),
      switchMap((action) => {
        return this.inputAppClientDBFacade.getBctxItemsOrderWithoutSize().pipe(
          take(1),
          map((res) => [action, res] as const),
        );
      }),
      switchMap(([action, orderKeys]) => {
        return this.roamingQcStoreFacade.acquireLock('SAVE_EPF_DATA').pipe(
          take(1),
          switchMap((res) => {
            if (res.status === 'SUCCESS') {
              return this.inputAppClientDBFacade.acquireUserDBLock('UPDATING_EPF_DETAILS_FOR_ROAMING_QC').pipe(
                take(1),
                switchMap((userDBLockDetails) => {
                  if (userDBLockDetails.status === 'SUCCESS') {
                    return from(this.persistentStorageService.get('[APP][INPUT_APP_ROAMING_QC_DB]')).pipe(
                      take(1),
                      switchMap((currentDB) => {
                        const updatedDB = RoamingQcUtil.updateEpfDetailsAndRecentCategoryList(
                          currentDB,
                          orderKeys,
                          action.bulkEvent,
                        );
                        return from(
                          this.persistentStorageService.set('[APP][INPUT_APP_ROAMING_QC_DB]', updatedDB),
                        ).pipe(
                          take(1),
                          map(() => {
                            this.inputAppClientDBFacade.releaseUserDBLock();
                            return new UpdateEpfDetailsAndRecentCategoryListAndReleaseLock(
                              updatedDB,
                              action.bulkEvent,
                              action.toBeRemovedModals,
                            );
                          }),
                        );
                      }),
                    );
                  } else {
                    this.inputAppAPIHelperUtil.showPopup(
                      this.translateService.instant('common.error'),
                      userDBLockDetails.message,
                    );
                    return of(new ReleaseRoamingQcLock());
                  }
                }),
              );
            } else {
              this.inputAppAPIHelperUtil.showPopup(this.translateService.instant('common.error'), res.message);
              return of(new RoamingQcNoAction());
            }
          }),
        );
      }),
    );
  });

  UpdateEpfDetailsAndRecentCategoryListAndReleaseLock$ = createEffect(() => {
    return this.action$.pipe(
      ofType<UpdateEpfDetailsAndRecentCategoryListAndReleaseLock>(
        RoamingQcStoreActionTypes.UpdateEpfDetailsAndRecentCategoryListAndReleaseLock,
      ),
      map((action) => {
        return new AcquireLockAndUpdateInProgressDetails(
          RoamingQcUtil.getRoundInProgressDetails(action.bulkEvent),
          action.toBeRemovedModals,
          action.bulkEvent,
        );
      }),
    );
  });

  AcquireLockAndUpdateInProgressDetails$ = createEffect(() => {
    return this.action$.pipe(
      ofType<AcquireLockAndUpdateInProgressDetails>(RoamingQcStoreActionTypes.AcquireLockAndUpdateInProgressDetails),
      switchMap((action) => {
        return this.roamingQcStoreFacade.acquireLock('SAVE_IN_PROGRESS_DETAILS').pipe(
          take(1),
          switchMap((res) => {
            if (res.status === 'SUCCESS') {
              if (action.chainWithEventAddition) {
                return this.inputAppClientDBFacade
                  .acquireUserDBLock('CLEARING_IN_PROGRESS_DETAILS_FOR_ROAMING_QC')
                  .pipe(
                    take(1),
                    switchMap((userDBLockRes) => {
                      if (userDBLockRes.status === 'SUCCESS') {
                        return this.roamingQcStoreFacade.updateInProgressDetailsInCache(action.inProgressDetails).pipe(
                          take(1),
                          map((updatedDB) => {
                            this.inputAppClientDBFacade.releaseUserDBLock();
                            return new UpdateInProgressDetailsAndReleaseLock(
                              updatedDB,
                              action.toBeRemovedModals,
                              action.chainWithEventAddition,
                            );
                          }),
                        );
                      } else {
                        this.inputAppAPIHelperUtil.showPopup(
                          this.translateService.instant('common.error'),
                          userDBLockRes.message,
                        );
                        return of(new ReleaseRoamingQcLock());
                      }
                    }),
                  );
              } else {
                return this.roamingQcStoreFacade.updateInProgressDetailsInCache(action.inProgressDetails).pipe(
                  take(1),
                  map((updatedDB) => {
                    return new UpdateInProgressDetailsAndReleaseLock(
                      updatedDB,
                      action.toBeRemovedModals,
                      action.chainWithEventAddition,
                    );
                  }),
                );
              }
            } else {
              this.inputAppAPIHelperUtil.showPopup(this.translateService.instant('common.error'), res.message);
              return of(new RoamingQcNoAction());
            }
          }),
        );
      }),
    );
  });

  UpdateInProgressDetailsAndReleaseLock$ = createEffect(() => {
    return this.action$.pipe(
      ofType<UpdateInProgressDetailsAndReleaseLock>(RoamingQcStoreActionTypes.UpdateInProgressDetailsAndReleaseLock),
      switchMap((action) => {
        if (action.chainWithEventAddition) {
          this.inputAppClientDBFacade.acquireLockAndThenAddBulkEvents(
            [action.chainWithEventAddition],
            false,
            { updateActualsOnly: false },
            {
              roamingQcAuditAddition: { toBeClosedModals: action.toBeRemovedModals },
            },
          );
          return of(new RoamingQcNoAction());
        } else {
          return from(this.inputAppAPIHelperUtil.dismissModals(action.toBeRemovedModals)).pipe(
            take(1),
            map(() => new RoamingQcNoAction()),
          );
        }
      }),
    );
  });

  AcquireLockAndClearInProgressDetails$ = createEffect(() => {
    return this.action$.pipe(
      ofType<AcquireLockAndClearInProgressDetails>(RoamingQcStoreActionTypes.AcquireLockAndClearInProgressDetails),
      switchMap((action) => {
        return this.roamingQcStoreFacade.acquireLock('CLEAR_IN_PROGRESS_DETAILS').pipe(
          take(1),
          switchMap((res) => {
            if (res.status === 'SUCCESS') {
              return from(this.roamingQcStoreFacade.clearInProgressDetailsInCache()).pipe(
                take(1),
                map((updatedDB) => new ClearInProgressDetailsAndReleaseLock(updatedDB, action.toBeRemovedModals)),
              );
            } else {
              this.inputAppAPIHelperUtil.showPopup(this.translateService.instant('common.error'), res.message);
              return of(new RoamingQcNoAction());
            }
          }),
        );
      }),
    );
  });

  ClearInProgressDetailsAndReleaseLock$ = createEffect(() => {
    return this.action$.pipe(
      ofType<ClearInProgressDetailsAndReleaseLock>(RoamingQcStoreActionTypes.ClearInProgressDetailsAndReleaseLock),
      switchMap((action) => {
        return from(this.inputAppAPIHelperUtil.dismissModals(action.toBeRemovedModals)).pipe(
          take(1),
          map(() => new RoamingQcNoAction()),
        );
      }),
    );
  });

  AcquireLockAndClearTransactionLogRecords$ = createEffect(() => {
    return this.action$.pipe(
      ofType<AcquireLockAndClearTransactionLogRecords>(
        RoamingQcStoreActionTypes.AcquireLockAndClearTransactionLogRecords,
      ),
      switchMap((action) => {
        return this.roamingQcStoreFacade.acquireLock('CLEAR_TRANSACTION_LOG_RECORDS').pipe(
          take(1),
          map((res) => {
            if (res.status === 'SUCCESS') {
              return new ClearTransactionLogRecordsAndReleaseLock();
            } else {
              this.inputAppAPIHelperUtil.showPopup(this.translateService.instant('common.error'), res.message);
              return new RoamingQcNoAction();
            }
          }),
        );
      }),
    );
  });

  ClearTransactionLogRecordsAndReleaseLock$ = createEffect(() => {
    return this.action$.pipe(
      ofType<ClearTransactionLogRecordsAndReleaseLock>(
        RoamingQcStoreActionTypes.ClearTransactionLogRecordsAndReleaseLock,
      ),
      switchMap((action) => {
        return from(this.modalCtrl.dismiss()).pipe(
          take(1),
          map(() => new RoamingQcNoAction()),
        );
      }),
    );
  });

  AcquireLockAndFetchTransactionLogRecords$ = createEffect(() => {
    return this.action$.pipe(
      ofType<AcquireLockAndFetchTransactionLogRecords>(
        RoamingQcStoreActionTypes.AcquireLockAndFetchTransactionLogRecords,
      ),
      switchMap((action) => {
        return this.roamingQcStoreFacade.acquireLock('UPDATE_TRNSACTION_LOG_RECORDS').pipe(
          take(1),
          map((res) => {
            if (res.status === 'SUCCESS') {
              return new FetchAndUpdateTransactionLogRecordsAndReleaseLock(
                action.startTime,
                action.endTime,
                action.updateInitialRoundInfo,
              );
            } else {
              this.inputAppAPIHelperUtil.showPopup(this.translateService.instant('common.error'), res.message);
              return new RoamingQcNoAction();
            }
          }),
        );
      }),
    );
  });

  FetchAndUpdateTransactionLogRecordsAndReleaseLock$ = createEffect(() => {
    return this.action$.pipe(
      ofType<FetchAndUpdateTransactionLogRecordsAndReleaseLock>(
        RoamingQcStoreActionTypes.FetchAndUpdateTransactionLogRecordsAndReleaseLock,
      ),
      switchMap((action) => {
        return from(
          this.inputAppAPIHelperUtil.presentLoading(
            action.updateInitialRoundInfo
              ? this.translateService.instant('roaming_qc_common.fetchingRoundInfo')
              : this.translateService.instant('roaming_qc_common.loadingTransactions'),
          ),
        ).pipe(
          take(1),
          map(() => action),
        );
      }),
      switchMap((action) => {
        return this.roamingQcStoreFacade.getTransactionsPayload(action.startTime, action.endTime).pipe(
          take(1),
          switchMap((res) => {
            return this.inputAppDataService.getRoamingQcTransactions(res).pipe(
              take(1),
              switchMap((transactionRecords) => {
                return from(this.inputAppAPIHelperUtil.dismissLoading()).pipe(
                  take(1),
                  map(() => {
                    const filteredTransactionRecords: TRESPONSE_RoamingQcTransactions =
                      this.roamingQcStoreFacade.filterTransactionForDateRange(
                        action.startTime,
                        action.endTime,
                        transactionRecords,
                      ); // filtering transactions for date range , response is +-1 day buffer
                    const transformedTransactionRecords =
                      this.roamingQcStoreFacade.transformTransactionRecords(filteredTransactionRecords);
                    if (action.updateInitialRoundInfo) {
                      return new UpdateInitialRoundInfoAndReleaseLock(transformedTransactionRecords);
                    } else return new UpdateTransactionLogRecordsAndReleaseLock(transformedTransactionRecords);
                  }),
                );
              }),
              catchError((error) => {
                this.logger.error(
                  'RoamingQcStoreEffects FetchAndUpdateTransactionLogRecordsAndReleaseLock - getRoamingQcTransactions API Error',
                  JSON.stringify(error),
                );
                return from(this.inputAppAPIHelperUtil.dismissLoading()).pipe(
                  take(1),
                  map(() => {
                    if (!action.updateInitialRoundInfo) {
                      this.inputAppAPIHelperUtil.showPopup(
                        this.translateService.instant('common.error'),
                        this.translateService.instant('roaming_qc_common.somethingWentWrongFetchingTxn'),
                      );
                      return new ReleaseRoamingQcLock();
                    } else if (action.updateInitialRoundInfo.defaultToEmptyTransactionsInCaseOfError) {
                      return new UpdateInitialRoundInfoAndReleaseLock([]);
                    } else {
                      this.roamingQcStoreFacade.releaseLock();
                      this.roamingQcStoreFacade.showErrorForRoundInit();
                      return new RoamingQcNoAction();
                    }
                  }),
                );
              }),
            );
          }),
        );
      }),
    );
  });

  UpdateInitialRoundInfoAndReleaseLock$ = createEffect(() => {
    return this.action$.pipe(
      ofType<UpdateInitialRoundInfoAndReleaseLock>(RoamingQcStoreActionTypes.UpdateInitialRoundInfoAndReleaseLock),
      switchMap((action) => {
        return this.inputAppClientDBFacade.getSelectedStationIdAndShiftId().pipe(
          take(1),
          map(({ stationId, shiftId }) => [action, stationId, shiftId] as const),
        );
      }),
      switchMap(([action, stationId, shiftId]) => {
        return this.roamingQcStoreFacade
          .updateIntitialRoundInfoInCache(
            RoamingQcUtil.getInitialRoundInfoFromTransactions(action.transactions),
            stationId,
            shiftId,
          )
          .pipe(
            take(1),
            map((updatedDB) => {
              return new UpdateStoreAndReleaseLockAndThenGoToInspection(updatedDB);
            }),
          );
      }),
    );
  });

  UpdateStoreAndReleaseLockAndThenGoToInspection$ = createEffect(() => {
    return this.action$.pipe(
      ofType<UpdateStoreAndReleaseLockAndThenGoToInspection>(
        RoamingQcStoreActionTypes.UpdateStoreAndReleaseLockAndThenGoToInspection,
      ),
      map((action) => {
        this.roamingQcStoreFacade.emitGoToInspectionSubscription(false);
        return new RoamingQcNoAction();
      }),
    );
  });
}
